import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import { useI18n } from "@/i18n";
import api from "@/api";

const initialForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  service: "",
  preferred_date: "",
  message: "",
};

export default function Contact() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState(initialForm);
  const [state, setState] = useState({ status: "idle", error: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "sending", error: "" });
    try {
      await api.post("/quotes", { ...form, language: lang });
      setState({ status: "success", error: "" });
      setForm(initialForm);
    } catch (err) {
      let msg = t("quote.error");
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((e) => e?.msg || "").join(" ");
      setState({ status: "error", error: msg });
    }
  };

  const services = [
    { v: "maintenance", label: t("quote.service.maintenance") },
    { v: "postconstruction", label: t("quote.service.postconstruction") },
    { v: "it", label: t("quote.service.it") },
    { v: "other", label: t("quote.service.other") },
  ];

  return (
    <div data-testid="page-contact" className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24">
      <div className="grid lg:grid-cols-12 gap-12">
        {/* Left: value + contact info */}
        <aside className="lg:col-span-5">
          <div className="text-[10px] tracking-[0.22em] uppercase text-stone-500 font-mono">{t("quote.eyebrow")}</div>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl tracking-tight text-stone-900 leading-[0.98]">{t("quote.title")}</h1>
          <p className="mt-6 text-lg text-stone-600">{t("quote.subtitle")}</p>

          <div className="mt-10 space-y-5">
            <ContactRow icon={Phone} label={t("contact.phone")} value="514-625-0359" href="tel:+15146250359" testid="contact-info-phone" />
            <ContactRow icon={Mail} label={t("contact.email")} value="gsmservicedemaintenance@gmail.com" href="mailto:gsmservicedemaintenance@gmail.com" testid="contact-info-email" />
            <ContactRow icon={MapPin} label={t("contact.area")} value={t("contact.area.value")} testid="contact-info-area" />
            <ContactRow icon={Clock} label={t("contact.hours")} value={t("contact.hours.value")} testid="contact-info-hours" />
          </div>

          <div className="mt-10 p-6 rounded-lg border border-stone-200 bg-white">
            <div className="text-xs font-mono tracking-widest uppercase text-[#B95736] mb-2">Laval · Québec</div>
            <p className="font-serif text-2xl text-stone-900 italic">« {t("footer.tagline")} »</p>
          </div>
        </aside>

        {/* Right: form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="p-8 md:p-10 rounded-lg border border-stone-200 bg-white space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <Field label={t("quote.name")} required>
                <input
                  data-testid="quote-name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  className="input"
                />
              </Field>
              <Field label={t("quote.company")}>
                <input
                  data-testid="quote-company"
                  value={form.company}
                  onChange={set("company")}
                  className="input"
                />
              </Field>
              <Field label={t("quote.email")} required>
                <input
                  data-testid="quote-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  className="input"
                />
              </Field>
              <Field label={t("quote.phone")} required>
                <input
                  data-testid="quote-phone"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  className="input"
                />
              </Field>
              <Field label={t("quote.service")} required>
                <select
                  data-testid="quote-service"
                  required
                  value={form.service}
                  onChange={set("service")}
                  className="input"
                >
                  <option value="">{t("quote.service.placeholder")}</option>
                  {services.map((s) => (
                    <option key={s.v} value={s.label}>{s.label}</option>
                  ))}
                </select>
              </Field>
              <Field label={t("quote.date")}>
                <input
                  data-testid="quote-date"
                  type="date"
                  value={form.preferred_date}
                  onChange={set("preferred_date")}
                  className="input"
                />
              </Field>
            </div>
            <Field label={t("quote.message")} required>
              <textarea
                data-testid="quote-message"
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                className="input resize-none"
              />
            </Field>

            {state.status === "success" && (
              <div data-testid="quote-success" className="flex items-start gap-3 p-4 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900">
                <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold">{t("quote.success.title")}</div>
                  <div className="text-sm">{t("quote.success.body")}</div>
                </div>
              </div>
            )}
            {state.status === "error" && (
              <div data-testid="quote-error" className="flex items-start gap-3 p-4 rounded-md bg-red-50 border border-red-200 text-red-900">
                <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
                <div className="text-sm">{state.error}</div>
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <div className="text-xs text-stone-500 font-mono">
                {lang === "fr" ? "* Champs requis" : "* Required fields"}
              </div>
              <button
                data-testid="quote-submit"
                type="submit"
                disabled={state.status === "sending"}
                className="btn-primary"
              >
                {state.status === "sending" ? t("quote.sending") : t("quote.submit")}
              </button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          padding: 0.75rem 1rem;
          background: #FAF9F6;
          border: 1px solid #E7E5E4;
          border-radius: 0.5rem;
          font-size: 0.95rem;
          color: #1C1917;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
          outline: none;
        }
        .input:focus {
          border-color: #B95736;
          background: #FFFFFF;
          box-shadow: 0 0 0 3px rgba(185, 87, 54, 0.15);
        }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-mono tracking-widest uppercase text-stone-600 block mb-2">
        {label}{required && <span className="text-[#B95736] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const content = (
    <>
      <div className="w-10 h-10 rounded-md bg-white border border-stone-200 flex items-center justify-center text-[#B95736] flex-shrink-0">
        <Icon size={16} strokeWidth={1.5} />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-stone-500 font-mono">{label}</div>
        <div className="mt-1 text-stone-900 group-hover:text-[#B95736] transition-colors">{value}</div>
      </div>
    </>
  );
  if (href) {
    return <a data-testid={testid} href={href} className="group flex items-start gap-4">{content}</a>;
  }
  return <div data-testid={testid} className="flex items-start gap-4">{content}</div>;
}
